import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff, Zap } from 'lucide-react';
import { showToast } from '../components/Toast';
import TwoFactorVerifyModal from '../components/TwoFactorVerifyModal';
import EmailSetupModal from '../components/EmailSetupModal';
import EmailVerifyModal from '../components/EmailVerifyModal';
import axios from 'axios';

// Helper function to get backend URL
const getBackendUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [systemLogo, setSystemLogo] = useState('');
  const [systemName, setSystemName] = useState('Fada App');
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showEmailSetup, setShowEmailSetup] = useState(false);
  const [showEmailVerify, setShowEmailVerify] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSystemSettings();
    // Load saved username from localStorage
    const savedUsername = localStorage.getItem('remembered_username');
    if (savedUsername) {
      setUsername(savedUsername);
    }
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await axios.get(`${backendUrl}/api/public/system-info`);
      setSystemLogo(response.data.system_logo || '');
      setSystemName(response.data.system_name || 'Fada App');
    } catch (error) {
      console.error('Error fetching system settings:', error);
      // Keep defaults on error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Check for existing device token
      const deviceToken = localStorage.getItem('device_token');
      
      const result = await login(username, password, deviceToken);
      
      // Check if 2FA TOTP is required (optional feature)
      if (result && result.requires_2fa && !result.requires_email) {
        setTempToken(result.temp_token);
        setShow2FAModal(true);
        setLoading(false);
        return;
      }
      
      // Check if email verification is required (mandatory)
      if (result && result.requires_email) {
        setTempToken(result.temp_token);
        
        // Check if user needs to setup email first
        if (result.needs_email_setup) {
          setShowEmailSetup(true);
        } else {
          // User has email, just verify code
          setEmailMasked(result.email_masked);
          setShowEmailVerify(true);
        }
        setLoading(false);
        return;
      }
      
      // Normal login completed (trusted device or email 2FA disabled)
      if (result.device_token) {
        localStorage.setItem('device_token', result.device_token);
      }
      localStorage.setItem('remembered_username', username);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSetupSuccess = (data) => {
    setShowEmailSetup(false);
    setTempToken(data.temp_token);
    setEmailMasked(data.email_masked);
    setShowEmailVerify(true);
  };

  const handleEmailVerifySuccess = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('remembered_username', username);
    // Save device token for 7-day trusted device
    if (data.device_token) {
      localStorage.setItem('device_token', data.device_token);
    }
    setShowEmailVerify(false);
    navigate('/dashboard');
    window.location.reload();
  };

  const handleEmailCancel = () => {
    setShowEmailSetup(false);
    setShowEmailVerify(false);
    setTempToken('');
    setLoading(false);
  };

  const handle2FASuccess = (data) => {
    // After TOTP 2FA, still need email verification
    // For now, complete login (email verification comes after TOTP if both are enabled)
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('remembered_username', username);
    setShow2FAModal(false);
    navigate('/dashboard');
    window.location.reload();
  };

  const handle2FACancel = () => {
    setShow2FAModal(false);
    setTempToken('');
    setLoading(false);
  };

  const handleSignupClick = () => {
    showToast.error('Apenas um revendedor pode te cadastrar!');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: 'url(/login-bg.png)',
      }}
    >
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          {systemLogo ? (
            <div className="inline-block mb-4">
              <img src={systemLogo} alt="Logo" className="h-20 w-auto mx-auto filter drop-shadow-2xl" />
            </div>
          ) : (
            <div className="inline-block p-4 bg-white rounded-full shadow-lg mb-4">
              <Zap className="w-12 h-12 text-purple-600" />
            </div>
          )}
        </div>

        {/* Login Card - Dark Mode */}
        <div className="bg-gray-900 rounded-2xl shadow-2xl p-8 border-2 border-gray-800">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Login</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-900/50 border-2 border-red-500 text-red-200 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Usuário
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border-2 border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Digite seu usuário"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-gray-800 border-2 border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Digite sua senha"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transform hover:-translate-y-0.5"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>

            {/* Forgot Password Link */}
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors hover:underline"
              >
                Esqueceu a senha?
              </button>
            </div>
          </form>

          {/* Sign up link */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Ainda não possui conta?{' '}
              <button
                onClick={handleSignupClick}
                className="text-purple-400 hover:text-purple-300 font-semibold hover:underline transition-colors"
              >
                Cadastre-se
              </button>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-purple-100 mt-6 text-sm">
          © 2025 {systemName}. Todos os direitos reservados.
        </p>
      </div>

      {/* 2FA Verification Modal */}
      <TwoFactorVerifyModal
        isOpen={show2FAModal}
        tempToken={tempToken}
        onSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
        api={axios.create({
          baseURL: getBackendUrl()
        })}
      />

      {/* Email Setup Modal */}
      <EmailSetupModal
        isOpen={showEmailSetup}
        tempToken={tempToken}
        onSuccess={handleEmailSetupSuccess}
        onCancel={handleEmailCancel}
        api={axios.create({
          baseURL: getBackendUrl()
        })}
      />

      {/* Email Verification Modal */}
      <EmailVerifyModal
        isOpen={showEmailVerify}
        tempToken={tempToken}
        emailMasked={emailMasked}
        onSuccess={handleEmailVerifySuccess}
        onCancel={handleEmailCancel}
        api={axios.create({
          baseURL: getBackendUrl()
        })}
      />
    </div>
  );
};

export default Login;
